import * as React from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Dismiss16Regular } from '@fluentui/react-icons';
import { cn } from '../utils/cn';
import { Button } from '../components/ui/Button';

type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | 'full' | string;

type ModalConfig = {
  Component?: React.ComponentType;
  element?: React.ReactNode;
  closable?: boolean;
  size?: ModalSize;
  naked?: boolean;
  showBackdrop?: boolean;
  style?: React.CSSProperties;
  className?: string;
  centered?: boolean;
  disableClickOutside?: boolean;
  mobileBehavior?: 'page' | 'modal'; // New config option
};

type ModalProps = {
  open: (e: ModalConfig) => void;
  close: () => void;
  Modal?: any;
};

const ModalComponent: React.FC<{
  isOpen: boolean;
  config: ModalConfig | null;
  close: () => void;
  setIsOpen: (val: boolean) => void;
  children?: React.ReactNode;
}> = ({ isOpen, config, close, setIsOpen, children }) => {
  const [isVisible, setIsVisible] = React.useState(true);
  const [shouldRender, setShouldRender] = React.useState(isOpen);
  const ANIMATION_DURATION = 300; // ms

  React.useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      requestAnimationFrame(() => setIsVisible(true));
    } else {
      setIsVisible(false);
      const timeout = setTimeout(
        () => setShouldRender(false),
        ANIMATION_DURATION
      );
      return () => clearTimeout(timeout);
    }
  }, [isOpen]);

  if (!config || !shouldRender) return null;

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  const useMobileBehavior = config.mobileBehavior === 'page' && isMobile;

  const animationBaseClasses = cn(
    'fixed z-50 bg-white shadow-lg transition-opacity transition-transform duration-300 ease-in-out',
    !useMobileBehavior && 'rounded-lg'
  );

  const modalClasses = cn(
    animationBaseClasses,
    config.naked ? 'p-0' : 'p-6',
    isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
  );

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={!config.disableClickOutside ? setIsOpen : undefined}
    >
      <Dialog.Portal>
        <Dialog.Overlay
          onClick={() => { }}
          className={`fixed inset-0 bg-black z-50 duration-300 ease-in-out opacity-70`}
        />
        <Dialog.Content
          className={cn(
            modalClasses,
            'border border-grey-800 z-[60]',
            'left-[50%] top-[50%] translate-x-[-50%] translate-y-[-50%]',
            'max-h-screen overflow-y-auto rounded-[20px] lg:rounded-[20px]',
            config?.className
          )}
          style={{ ...config?.style }}
          aria-describedby={undefined}
          onPointerDownOutside={(e) => {
            if (config.disableClickOutside) {
              e.preventDefault();
            }
          }}
        >
          <Dialog.Title></Dialog.Title>
          {config.closable && (
            <Button
              onClick={close}
              size="icon"
              variant="ghost"
              className={cn(
                'h-9 w-9 absolute right-2',
                useMobileBehavior ? 'top-4' : 'top-2'
              )}
            >
              <Dismiss16Regular />
            </Button>
          )}
          {config.Component && <config.Component />}
          {config.element}
          {children}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export function useModal(): ModalProps {
  const [isOpen, setIsOpen] = React.useState(false);
  const [config, setConfig] = React.useState<ModalConfig | null>(null);

  const open = React.useCallback(
    ({ centered = true, mobileBehavior = 'page', ...rest }: ModalConfig) => {
      setConfig({ centered, mobileBehavior, ...rest });
      setIsOpen(true);
    },
    []
  );

  const close = React.useCallback(() => {
    setIsOpen(false);
    setConfig(null);
  }, []);

  const Modal = React.useCallback(
    ({ children }: { children?: React.ReactNode }) => (
      <ModalComponent
        isOpen={isOpen}
        config={config}
        close={close}
        setIsOpen={setIsOpen}
      >
        {children}
      </ModalComponent>
    ),
    [isOpen, config, close]
  );

  return { open, close, Modal };
}
